import type { Fingering, HoleDef, HoleId, HoleState, OcarinaVariant } from './types';

/**
 * Twelve-hole transverse ocarina.
 *
 * Every fingering here is marked `verified: false`. The diatonic core follows
 * the standard chart for a 12-hole alto C, but sub-hole assignment and the
 * cross-fingerings for chromatic notes differ between makers. The app must show
 * these as "check against your instrument" and let the user override them, which
 * is why the flag travels with the data rather than living in a comment.
 */

const HOLE_ORDER: HoleId[] = [
  'L1',
  'L2',
  'L3',
  'L4',
  'R1',
  'R2',
  'R3',
  'R4',
  'LT',
  'RT',
  'SL',
  'SR',
];

/** Layout in a 0-100 box, as the widget draws it. */
const HOLES_12: HoleDef[] = [
  { id: 'L1', label: 'Left index', side: 'top', x: 26, y: 38, r: 6, hand: 'L', finger: 'index' },
  { id: 'L2', label: 'Left middle', side: 'top', x: 38, y: 34, r: 6, hand: 'L', finger: 'middle' },
  { id: 'L3', label: 'Left ring', side: 'top', x: 50, y: 34, r: 6, hand: 'L', finger: 'ring' },
  { id: 'L4', label: 'Left pinky', side: 'top', x: 61, y: 38, r: 5, hand: 'L', finger: 'pinky' },
  { id: 'R1', label: 'Right index', side: 'top', x: 26, y: 58, r: 6, hand: 'R', finger: 'index' },
  { id: 'R2', label: 'Right middle', side: 'top', x: 38, y: 62, r: 6, hand: 'R', finger: 'middle' },
  { id: 'R3', label: 'Right ring', side: 'top', x: 50, y: 62, r: 6, hand: 'R', finger: 'ring' },
  { id: 'R4', label: 'Right pinky', side: 'top', x: 61, y: 58, r: 5, hand: 'R', finger: 'pinky' },
  { id: 'LT', label: 'Left thumb', side: 'bottom', x: 34, y: 78, r: 6, hand: 'L', finger: 'thumb' },
  {
    id: 'RT',
    label: 'Right thumb',
    side: 'bottom',
    x: 54,
    y: 78,
    r: 6,
    hand: 'R',
    finger: 'thumb',
  },
  { id: 'SL', label: 'Left sub-hole', side: 'top', x: 19, y: 44, r: 3.5, hand: 'L', finger: 'sub' },
  {
    id: 'SR',
    label: 'Right sub-hole',
    side: 'top',
    x: 68,
    y: 52,
    r: 3.5,
    hand: 'R',
    finger: 'sub',
  },
];

const ALL_CLOSED: Record<HoleId, HoleState> = Object.fromEntries(
  HOLE_ORDER.map((id) => [id, 'closed' as HoleState]),
) as Record<HoleId, HoleState>;

/** Builds a fingering by naming the holes that are open (or half covered). */
function fing(open: HoleId[], half: HoleId[] = [], note?: string): Fingering {
  const holes: Record<HoleId, HoleState> = { ...ALL_CLOSED };
  for (const id of open) holes[id] = 'open';
  for (const id of half) holes[id] = 'half';
  return { holes, verified: false, ...(note !== undefined ? { note } : {}) };
}

/**
 * Alto C chart. Holes open progressively from the right pinky upward, which is
 * the pattern a player learns first.
 */
const AC12_FINGERINGS: Record<string, Fingering> = {
  A4: fing([]),
  'A#4': fing(['SR']),
  Bb4: fing(['SR']),
  B4: fing(['SR', 'SL']),
  C5: fing(['SL', 'SR']),
  'C#5': fing(['SL', 'SR'], ['R4'], 'Half-hole; varies by maker'),
  Db5: fing(['SL', 'SR'], ['R4'], 'Half-hole; varies by maker'),
  D5: fing(['SL', 'SR', 'R4']),
  'D#5': fing(['SL', 'SR', 'R4'], ['R3']),
  Eb5: fing(['SL', 'SR', 'R4'], ['R3']),
  E5: fing(['SL', 'SR', 'R4', 'R3']),
  F5: fing(['SL', 'SR', 'R4', 'R3', 'R2']),
  'F#5': fing(['SL', 'SR', 'R4', 'R3', 'R1'], [], 'Cross-fingering: R2 stays closed'),
  Gb5: fing(['SL', 'SR', 'R4', 'R3', 'R1'], [], 'Cross-fingering: R2 stays closed'),
  G5: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1']),
  'G#5': fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1'], ['L4']),
  Ab5: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1'], ['L4']),
  A5: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4']),
  'A#5': fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L2'], [], 'Cross-fingering: L3 closed'),
  Bb5: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L2'], [], 'Cross-fingering: L3 closed'),
  B5: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3']),
  C6: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2']),
  'C#6': fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2'], ['L1']),
  Db6: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2'], ['L1']),
  D6: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2', 'L1']),
  'D#6': fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2', 'L1'], ['RT']),
  Eb6: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2', 'L1'], ['RT']),
  E6: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2', 'L1', 'RT']),
  F6: fing(['SL', 'SR', 'R4', 'R3', 'R2', 'R1', 'L4', 'L3', 'L2', 'L1', 'RT', 'LT']),
};

/** Six-hole English pendant: a different system, with its own small chart. */
const PENDANT6_HOLES: HoleDef[] = [
  { id: 'L1', label: 'Left index', side: 'top', x: 30, y: 40, r: 8, hand: 'L', finger: 'index' },
  { id: 'L2', label: 'Left middle', side: 'top', x: 48, y: 36, r: 8, hand: 'L', finger: 'middle' },
  { id: 'R1', label: 'Right index', side: 'top', x: 66, y: 40, r: 8, hand: 'R', finger: 'index' },
  { id: 'R2', label: 'Right middle', side: 'top', x: 48, y: 60, r: 8, hand: 'R', finger: 'middle' },
  { id: 'LT', label: 'Left thumb', side: 'bottom', x: 38, y: 78, r: 7, hand: 'L', finger: 'thumb' },
  {
    id: 'RT',
    label: 'Right thumb',
    side: 'bottom',
    x: 58,
    y: 78,
    r: 7,
    hand: 'R',
    finger: 'thumb',
  },
];

const PENDANT_ORDER: HoleId[] = ['L1', 'L2', 'R1', 'R2', 'LT', 'RT'];
const PENDANT_CLOSED: Record<HoleId, HoleState> = Object.fromEntries(
  PENDANT_ORDER.map((id) => [id, 'closed' as HoleState]),
) as Record<HoleId, HoleState>;

function pend(open: HoleId[], half: HoleId[] = []): Fingering {
  const holes: Record<HoleId, HoleState> = { ...PENDANT_CLOSED };
  for (const id of open) holes[id] = 'open';
  for (const id of half) holes[id] = 'half';
  return { holes, verified: false };
}

const PENDANT6_FINGERINGS: Record<string, Fingering> = {
  C5: pend([]),
  D5: pend(['R2']),
  E5: pend(['R2', 'R1']),
  F5: pend(['R2', 'R1', 'RT']),
  G5: pend(['R2', 'R1', 'RT', 'LT']),
  A5: pend(['R2', 'R1', 'RT', 'LT', 'L2']),
  B5: pend(['R2', 'R1', 'RT', 'LT', 'L2', 'L1']),
  C6: pend(['R2', 'R1', 'RT', 'LT', 'L2', 'L1']),
};

/** The transposing variants share the alto C chart, shifted in pitch. */
function transposedVariant(
  id: string,
  name: string,
  semitones: number,
  range: { low: number; high: number },
): OcarinaVariant {
  return {
    id,
    name,
    holes: HOLES_12,
    fingerings: AC12_FINGERINGS,
    range,
    transposeFromAC: semitones,
  };
}

export const OCARINA_VARIANTS: OcarinaVariant[] = [
  {
    id: 'ac12',
    name: '12-hole alto C',
    holes: HOLES_12,
    fingerings: AC12_FINGERINGS,
    range: { low: 69, high: 89 },
    transposeFromAC: 0,
  },
  transposedVariant('sc12', '12-hole soprano C', 12, { low: 81, high: 101 }),
  transposedVariant('ag12', '12-hole alto G', -5, { low: 64, high: 84 }),
  transposedVariant('bc12', '12-hole bass C', -12, { low: 57, high: 77 }),
  {
    id: 'pendant6',
    name: '6-hole pendant',
    holes: PENDANT6_HOLES,
    fingerings: PENDANT6_FINGERINGS,
    range: { low: 72, high: 84 },
    transposeFromAC: 0,
  },
];

export const OCARINA_HOLE_ORDER = HOLE_ORDER;

/** True when any fingering in a variant still needs checking on a real instrument. */
export function hasUnverifiedFingerings(variant: OcarinaVariant): boolean {
  return Object.values(variant.fingerings).some((f) => !f.verified);
}
